import React, { CSSProperties, FunctionComponent, useEffect, useLayoutEffect, useReducer, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Badge, Breadcrumb, Button, Card, Checkbox, Icon, isMobileDevice, isTabletDevice, Modal } from 'rs-emd-ui-atoms';
import { Observable, Subject } from 'rxjs';
import { MediaCarousel, PriceArea, PriceBreakTable, PunchoutInfo, TechnicalDocuments } from '.';
import { ProductDetailPage } from '../../../../generated/graphql';
import { pdpService } from '../../../../services/pdp-service';
import { clickEvent } from '../../../../tagging/ensighten';
import { getCookie, getLabel } from '../../helpers/html.utils';
import { includeVat } from '../../helpers/user-settings.utils';
import { DocumentType } from '../../models/common/document-item.model';
import { IStock, StockStatus } from '../../models/common/stock.model';
import { IContextInfo } from '../../models/master/context-info.model';
import { IPDPCADData } from '../../models/pdp/cad-documents/pdp-cad-data.model';
import { IPDPCalibration } from '../../models/pdp/pdp-calibration.model';
import { IPDPDocuments } from '../../models/pdp/pdp-documents.model';
import { IPDPMedia } from '../../models/pdp/pdp-media.model';
import { IPDPPriceBreakItem } from '../../models/pdp/pdp-price-break-item.model';
import { IPDPPricing } from '../../models/pdp/pdp-pricing.model';
import { PdpStickerPosition } from '../../models/pdp/pdp-stickers.model';
import { IPDPTitle } from '../../models/pdp/pdp-title.model';
import { IFacetFilter } from '../../models/plp/input/search-selector.model';
import { IBasketService } from '../../services/basket/basket-service';
import { IPartsListService } from '../../services/parts-list/parts-list-service';
import { IPLPService } from '../../services/plp/plp-service';
import { IPricingService } from '../../services/pricing/pricing-service';
import { IProductService } from '../../services/product/product-service';
import { IGetStockDataRequest, IGetStockDataResult } from '../../services/product/product.dto';
import { DTPStockBadge, DTPStockInfo, MetaDataAndTagging, RecentlyViewedComponent, StockStatusCard } from '../shared';
import { ReactComponent as DesignSparkLogo } from './../../images/pdp/design-spark-logo.svg';
import DesignSparkImageTablet from './../../images/pdp/design-spark-tablet.jpeg';
import DesignSparkImage from './../../images/pdp/design-spark.jpeg';
import rspro from './../../images/rs-pro.svg';
import { AlternativeProducts } from './alternative-products/alternative-products-component';
import { PDPQueryType, PDPRequest, PDPResponse } from './pdp.dto';
import styles from './product-detail-page-component.module.scss';

type ProductDetailPageProps = {
  data: ProductDetailPage;
  contextInfo: IContextInfo;
  documents?: IPDPDocuments; //WS implementation
  subscribeToNewDocuments?: () => void; //WS implementation
  requestSubject?: Subject<PDPRequest>;
  responseObservable?: Observable<PDPResponse>;
  partsListService: IPartsListService;
  pricingService: IPricingService;
  basketService: IBasketService;
  productService: IProductService;
  plpService: IPLPService;
  getStockData: (options: IGetStockDataRequest) => Promise<IGetStockDataResult>;
  breadCrumbHasBackBtn?: boolean;
};

type State = {
  doProductDetailsNeedTruncation: boolean;
  isProductDetailsOpen: boolean;
  titleArea?: IPDPTitle | null;
  priceArea?: IPDPPricing | null;
  stockArea?: IStock | null;
  calibrationArea?: IPDPCalibration | null;
  selectedPriceBreakIndex?: number;
  selectedQuantity?: number;
  documentsArea: IPDPDocuments;
  cadDocuments?: IPDPCADData;
  height?: number;
  isSimilarProductsModalOpen: boolean;
  similarProductsSelectedAttributes: (string | number)[];
  totalFilteredProducts?: number;
  filteredFacets?: IFacetFilter[];
  euElrProductInformationSheet?: { callNum: number; url?: string };
};

export enum PDPActionType {
  ToggleProductDetails = 'ToggleProductDetails',
  UpdateSelectedQty = 'UpdateSelectedQty',
  UpdateStockData = 'UpdateStockData',
  Resize = 'Resize',
  ToggleSimilarProductsModaOpen = 'ToggleSimilarProductsModaOpen',
  SelectSimilarProductAttribute = 'SelectSimilarProductAttribute',
  SetTotalFilteredProducts = 'SetTotalFilteredProducts',
  SetDoProductDetailsNeedTruncation = 'SetDoProductDetailsNeedTruncation',
}

type Action =
  | { type: PDPActionType.ToggleProductDetails; isOpen: boolean }
  | { type: PDPActionType.UpdateSelectedQty; quantity?: number }
  | { type: PDPActionType.UpdateStockData; stockArea?: IStock | null }
  | { type: PDPActionType.ToggleSimilarProductsModaOpen; isOpen: boolean }
  | { type: PDPActionType.SelectSimilarProductAttribute; keys: (string | number)[] }
  | { type: PDPActionType.SetTotalFilteredProducts; total: number; filteredFacets: IFacetFilter[] }
  | { type: PDPActionType.SetDoProductDetailsNeedTruncation; doProductDetailsNeedTruncation: boolean }
  | PDPResponse;

export const ProductDetailPageComponent: FunctionComponent<ProductDetailPageProps> = (props: ProductDetailPageProps) => {
  const [incVat, setIncVat] = useState<boolean>(false);
  const designSparkTitleRef = useRef<HTMLDivElement>(null);
  const [isWebview, setIsWebview] = useState(true);

  const [state, dispatch] = useReducer(reducer, {
    doProductDetailsNeedTruncation: true,
    isProductDetailsOpen: false,
    titleArea: props.data.titleArea,
    priceArea: props.data.priceArea,
    stockArea: props.data.stockArea,
    calibrationArea: props.data.calibrationArea,
    selectedQuantity: props.data.priceArea?.minimumOrderQuantity,
    documentsArea: props.data.documentsArea,
    height: 0,
    isSimilarProductsModalOpen: false,
    similarProductsSelectedAttributes: [],
    euElrProductInformationSheet: { callNum: 0, url: undefined },
  });

  let history = useNavigate();
  let location = useLocation();

  let dtpStockBadge = <DTPStockBadge labels={props.data.labels} stockArea={state.stockArea} />;
  let dtpStockInfo = <DTPStockInfo labels={props.data.labels} stockArea={state.stockArea} showBullets={true} />;

  const phone = state.stockArea?.otherInfo?.find(({ key }) => key === 'phone')?.value;
  const email = state.stockArea?.otherInfo?.find(({ key }) => key === 'email')?.value;

  const unavailableStockInfo = (
    <Card className={styles['unavailable-stock-info']}>
      <p className={styles['unavailable-title']}>{state.stockArea?.title}</p>
      <div className={styles['details-wrapper']}>
        <p className={styles['unavailable-subtitle']}>{state.stockArea?.infoMessage}</p>
        {email && (
          <a href={`mailto:${email}`} className={`${styles['contact-detail']} ${styles.email}`}>
            <Icon name='envelope' />
            <span>{email}</span>
          </a>
        )}
        {phone && (
          <a href={`tel:${phone}`} className={styles['contact-detail']}>
            <Icon name='phone' />
            <span>{phone}</span>
          </a>
        )}
      </div>
    </Card>
  );
  // determine whether product details require truncation or not
  useLayoutEffect(() => {
    function setProductDetailsTruncation() {
      const productInfo =
        isMobileDevice() || isTabletDevice()
          ? document.querySelector('#product-info-not-desktop')
          : document.querySelector('#product-info-desktop');

      if (productInfo) {
        const detailsText = productInfo.querySelector('#details-text');

        if (detailsText) {
          dispatch({
            type: PDPActionType.SetDoProductDetailsNeedTruncation,
            doProductDetailsNeedTruncation: detailsText.scrollHeight > detailsText.getBoundingClientRect().height,
          });
        }
      }
    }

    setProductDetailsTruncation();
  }, []);

  // subscribe to observale
  useLayoutEffect(() => {
    const resSubscription = props.responseObservable?.subscribe((res) => {
      dispatch(res);
    });

    if (props.data.titleArea?.stockCode) {
      // START: documents async queries
      // RS documents
      props.requestSubject?.next({ type: PDPQueryType.GetDataSheets, stockCode: props.data.titleArea?.stockCode, documentsType: '1' });

      // CAD documents
      const cadDocs = props.data.documentsArea.documents.find(
        (d) => d.documentType === DocumentType.TraceParts3DCad && d.url === 'cadDocuments'
      );
      if (cadDocs)
        props.requestSubject?.next({
          type: PDPQueryType.GetCadDocuments,
          stockCode: props.data.titleArea?.stockCode,
          tracePartsPartId: cadDocs.tracePartsPartId,
        });
      // END: documents async queries

      // stock async query
      props.getStockData({ stockCode: props.data.titleArea?.stockCode }).then((res) => {
        if (res.stockArea) {
          dispatch({ type: PDPActionType.UpdateStockData, stockArea: res.stockArea });
        }
      });
    }

    return () => {
      // Cleanup & unsubscribe
      resSubscription?.unsubscribe();
    };
  }, [props, props.data.documentsArea.documents, props.data.titleArea, props.documents, props.requestSubject, props.responseObservable]);

  useLayoutEffect(() => {
    setIncVat(includeVat());

    // subscribe to technical documents
    if (props.subscribeToNewDocuments) props.subscribeToNewDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    // webview set in cookie?
    setIsWebview(Boolean(JSON.parse(getCookie('webview') ?? '0'))); // will hide the header
  }, []);

  let expandSearchBtns = (
    <div className={styles['expand-search-btns']} data-verbolia-id='expand-search-btns'>
      <Button
        buttonType='outline'
        text={getLabel(props.data.labels, 'view_all_in_category').replace(
          '[category]',
          props.data.breadcrumb.links[props.data.breadcrumb.links.length - 1].text
        )}
        href={props.data.breadcrumb.links[props.data.breadcrumb.links.length - 1].url}
        onClick={() => rsDeliversClickTracker('View_Category_PDP')}
        data-verbolia-id='view-all-in-category'
      />
      {props.data.attributesArea.attributes.length > 0 && (
        <Button
          buttonType='outline'
          text={getLabel(props.data.labels, 'search_for_similar_products')}
          onClick={() => {
            rsDeliversClickTracker('Find_Similar_PDP');
            dispatch({ type: PDPActionType.ToggleSimilarProductsModaOpen, isOpen: true });
          }}
        />
      )}
    </div>
  );

  // get number of selected attributes for similar product search fillters
  useEffect(() => {
    function getSelectedFacets(selectedAttributesKeys: (string | number)[]) {
      // return only attributes with populated nameIds and valueIds
      let selectedAttributes = props.data.attributesArea.attributes.filter(
        (a) => selectedAttributesKeys.includes(a.name) && a.nameId && a.nameId?.length > 0 && a.valueId && a.valueId.length > 0
      );

      return selectedAttributes.map((a) => {
        return { facetId: a.nameId ?? '', facetValueId: a.valueId ?? '' };
      });
    }

    function previewNewFilters(selectedAttributesKeys: (string | number)[]) {
      let facets = getSelectedFacets(selectedAttributesKeys);

      if (facets.length > 0) {
        props.plpService
          .getPreviewFiltersTotalProducts({
            slug: props.data.breadcrumb.links[props.data.breadcrumb.links.length - 1].url.split('/browse/')[1],
            selector: {
              facets: facets,
            },
          })
          .then((res) =>
            dispatch({ type: PDPActionType.SetTotalFilteredProducts, total: res.summary?.totalProducts ?? 0, filteredFacets: facets })
          );
      } else {
        dispatch({ type: PDPActionType.SetTotalFilteredProducts, total: 0, filteredFacets: [] });
      }
    }

    previewNewFilters(state.similarProductsSelectedAttributes);
  }, [props.data.attributesArea.attributes, props.data.breadcrumb.links, props.plpService, state.similarProductsSelectedAttributes]);

  let productTypes = [props.data.productTypeArea, ...(props.data.productTypeArea?.secondaryMessages ?? [])].filter(
    (productType) => productType?.isVisible
  );

  const isBetterWorldProduct = productTypes.some((p) => p.mode === 'SUSTAINABLE');

  let productCards = (
    <div className={styles['product-cards']}>
      {/* Live stock */}
      {state.stockArea?.isUsingDTP && ( //check if it is to be included when DTP is enabled
        <>
          {state.stockArea && !state.stockArea.isUsingDTP && (
            <Card className={styles.livestock} radius={0} cardTitle={getLabel(props.data.labels, 'live_stock_heading')}>
              {/*Parts finder not for MVP */}
              {state.stockArea.statusCode !== StockStatus.Instock && (
                <Button text='Parts finder' buttonType='tertiary' className={styles['btn-parts-finder']}></Button>
              )}
            </Card>
          )}
        </>
      )}

      {(isMobileDevice() || isTabletDevice()) && (
        <div
          className={
            isBetterWorldProduct ? styles['lte-tablet-better-world-banner-visible'] : styles['lte-tablet-better-world-banner-not-visible']
          }>
          {getBetterWorldBanner()}
        </div>
      )}

      {/* Technical Documents */}
      {(state.documentsArea.documents.length > 0 || (state.cadDocuments && state.cadDocuments.tracePartsData.documents.length > 0)) && (
        <TechnicalDocuments
          labels={props.data.labels}
          documentsArea={state.documentsArea}
          titleArea={props.data.titleArea}
          cadDocuments={state.cadDocuments}
          downloadCADDocument={(docId) => getTracePartsDocumentUrl(docId)}
        />
      )}

      {/* Specifications */}
      {props.data.attributesArea.attributes.length > 0 && (
        <Card
          className={styles.specifications}
          radius={0}
          cardTitle={getLabel(props.data.labels, 'specifications_heading')}
          titleClassName={styles['card-title']}
          data-verbolia-id='product-detail-page-specifications'>
          <div className={styles['spec-list']} data-verbolia-id='spec-list'>
            {props.data.attributesArea.attributes.map((s, index) => {
              return (
                <div className={styles.spec} key={`${index}_${s.name}`}>
                  <p className={`snippet ${styles.label}`}>
                    {s.name}
                    {s.nameId === '17954' ? (
                      <span>
                        {' '}
                        ({<Link to={getLabel(props.data.labels, 'better_world_url')}>{getLabel(props.data.labels, 'find_out_more')}</Link>})
                      </span>
                    ) : (
                      ''
                    )}
                  </p>
                  {/** brand */}
                  {s.nameId === '4390' ? (
                    <a
                      className={`snippet ${styles.value} ${styles['brand-link-spec']}`}
                      href={`/productlist/search?query=${s.value.toLowerCase()}`}
                      onClick={() =>
                        props.data.titleArea?.brand &&
                        props.productService.brandTrackerClickEvent('BrandSearchPDPspecification', props.data.titleArea.brand)
                      }>
                      {s.value}
                    </a>
                  ) : (
                    <p className={`snippet ${styles.value}`}>{s.value}</p>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Product Details */}
      {props.data.descriptionArea.descriptions.filter((d) => d.title || d.description).length > 0 && (
        <Card
          className={styles.details}
          radius={0}
          cardTitle={getLabel(props.data.labels, 'product_details_heading')}
          titleClassName={styles['card-title']}
          data-verbolia-id='product-detail-page-details'>
          <div
            id='details-text'
            className={`${styles.text} ${state.doProductDetailsNeedTruncation && !state.isProductDetailsOpen ? styles.truncated : ''}`}>
            {props.data.descriptionArea.descriptions.map((d) => {
              return (
                <>
                  {/* Need surrounding spans to use line-clamp */}
                  <span>
                    <h3 className={styles['range-heading']} dangerouslySetInnerHTML={{ __html: d.title }}></h3>
                  </span>
                  <span>
                    <p className={styles['range-desc']} dangerouslySetInnerHTML={{ __html: d.description }}></p>
                  </span>
                </>
              );
            })}
          </div>
          {state.doProductDetailsNeedTruncation && (
            <div className={styles['product-details-read-btns']}>
              {!state.isProductDetailsOpen && (
                <Button
                  className={`${styles['read-btn']} ${styles.more}`}
                  buttonType='text'
                  text={getLabel(props.data.labels, 'read_more')}
                  onClick={() => dispatch({ type: PDPActionType.ToggleProductDetails, isOpen: true })}></Button>
              )}
              {state.isProductDetailsOpen && (
                <Button
                  className={`${styles['read-btn']}`}
                  buttonType='text'
                  text={getLabel(props.data.labels, 'read_less')}
                  onClick={() => dispatch({ type: PDPActionType.ToggleProductDetails, isOpen: false })}></Button>
              )}
            </div>
          )}
        </Card>
      )}

      <div
        className={
          props.data.config.showDesignSparkArea
            ? styles['lte-tablet-design-spark-banner-visible']
            : styles['lte-tablet-design-spark-banner-not-visible']
        }>
        {getDesignSparkBlock()}
      </div>

      {/* Alternative Products */}
      <div className={styles['alternative-products-lte-tablet']} data-verbolia-id='alternative-products-tablet'>
        {getAlternativeProductBlock()}
      </div>
    </div>
  );

  useLayoutEffect(() => {
    if (props.contextInfo.isBot) return;
    pdpService.addRecentlyViewed(
      props.data.titleArea?.stockCode ?? '',
      props.data.titleArea?.brand ?? '',
      props.data.titleArea?.familyId ?? 0
    );
  }, [props.contextInfo.isBot, props.data.titleArea]);

  const backProps = props.breadCrumbHasBackBtn
    ? {
        backArrowText: getLabel(props.data.labels, 'back_to_results'),
        backCallback: () => {
          window.history.back();
          clickBackToResults();
        },
      }
    : {};

  return (
    <div
      className={styles['pdp-container']}
      style={{ '--design-spark-title-height': designSparkTitleRef.current?.clientHeight + 'px' } as CSSProperties}>
      {/* Head data */}
      <MetaDataAndTagging metaData={props.data.metaData} tagging={props.data.tagging} />
      <Helmet>
        <script>{`function showLine(stockCode) {window.location.href = '/productpage/productpage?stockcode=' + stockCode;}`}</script>
      </Helmet>

      {props.data.titleArea && (
        <div className={`inner-page-wrapper ${styles['product-header']}`}>
          {!isWebview && (
            <div className={styles.breadcrumb} data-verbolia-id='product-detail-page-breadcrumb'>
              <Breadcrumb links={props.data.breadcrumb.links} {...backProps} />
            </div>
          )}

          {/* Restricted/Barred products */}
          {(props.data.punchoutInfoArea.isBarred || props.data.punchoutInfoArea.isRestricted) && (
            <PunchoutInfo labels={props.data.labels} isRestricted={props.data.punchoutInfoArea.isRestricted} />
          )}

          <Card radius={0} className={styles['title-card']}>
            <div className={styles['title-block-wrapper']} data-verbolia-id='title-block-wrapper'>
              <div className={styles['title-block']}>
                {productTypes.length > 0 && (
                  <div className={styles.info} data-verbolia-id='product-detail-page-badges'>
                    {productTypes.map((productType) => {
                      return (
                        <Badge
                          key={`product_type_${productType.title}`}
                          colour={productType.mode === 'WARNING' ? 'yellow' : 'green'}
                          icon={productType.mode ? (productType.mode.toLowerCase() as 'warning' | 'sustainable') : undefined}
                          href={productType.mode === 'SUSTAINABLE' ? getLabel(props.data.labels, 'better_world_url') : undefined}>
                          {productType.title}
                        </Badge>
                      );
                    })}
                  </div>
                )}
                <h1 className={`title ${styles.title}`}>{state.titleArea?.title}</h1>
                <div className={`${styles.info} ${styles['prod-info']}`}>
                  {state.titleArea?.formattedStockCode && (
                    <span>
                      <span className={styles['prod-info-title']}>{getLabel(props.data.labels, 'stock_code')}: </span>
                      {state.titleArea.formattedStockCode}
                    </span>
                  )}
                  {props.data.titleArea.brand && (
                    <span>
                      <span className={styles['prod-info-title']}>{getLabel(props.data.labels, 'brand')}: </span>
                      <a
                        href={`/productlist/search?query=${encodeURIComponent(props.data.titleArea.brand.toLowerCase())}`}
                        onClick={() =>
                          props.data.titleArea?.brand &&
                          props.productService.brandTrackerClickEvent('BrandSearchPDPheader', props.data.titleArea.brand)
                        }>
                        {props.data.titleArea.brand}
                      </a>
                    </span>
                  )}
                  {props.data.titleArea.mpn && (
                    <span>
                      <span className={styles['prod-info-title']}>{getLabel(props.data.labels, 'mpn')}</span>: {props.data.titleArea.mpn}
                    </span>
                  )}
                  {props.data.titleArea.impaCode && props.data.titleArea.impaCode.length > 0 && (
                    <span>
                      <span className={styles['prod-info-title']}>{getLabel(props.data.labels, 'impa')}: </span>
                      {props.data.titleArea.impaCode}
                    </span>
                  )}
                  {props.data.titleArea.distrelecProductId && props.data.titleArea.distrelecProductId.length > 0 && (
                    <span>
                      <span className={styles['prod-info-title']}>{getLabel(props.data.labels, 'distrelec_product_id')}: </span>
                      {props.data.titleArea.distrelecProductId}
                    </span>
                  )}
                  {props.data.titleArea.hsnCode && (
                    <span>
                      <span className={styles['prod-info-title']}>{getLabel(props.data.labels, 'hsn')}</span>:{' '}
                      {props.data.titleArea.hsnCode}
                    </span>
                  )}
                </div>
              </div>
              {props.data.mediaArea?.vendorLogo && (
                <a
                  href={`/productlist/search?query=${props.data.titleArea.brand}`}
                  onClick={() =>
                    props.productService.clickEvent({
                      __Type: 'buttonTracker',
                      __Info: 'BrandSearchLogo',
                      __Result: props.data.titleArea?.stockCode,
                    })
                  }>
                  <img alt='brand-logo' src={props.data.mediaArea?.vendorLogo.url} className={styles['brand-logo']} />
                </a>
              )}
            </div>
          </Card>
        </div>
      )}
      <div className={styles['product-body']} data-verbolia-id='product-detail-page-product-body'>
        <div className={`inner-page-wrapper`}>
          <div className={styles['top-area']} data-verbolia-id='top-area'>
            <div className={styles['media-area']} data-verbolia-id='media-area'>
              {props.data.mediaArea && (
                <MediaCarousel
                  media={getMedia()}
                  labels={props.data.labels}
                  sticker={props.data.stickersArea.stickers.find((s) => s.position === PdpStickerPosition.DescriptionRight)}
                  productService={props.productService}></MediaCarousel>
              )}
              {/** Expand search buttons tablet + */}
              {!isMobileDevice() && expandSearchBtns}

              <div id='product-info-desktop' className={styles['product-info-desktop']}>
                {productCards}
              </div>
            </div>

            <div className={styles['price-area-wrapper']} data-verbolia-id='price-area-wrapper'>
              <div className={styles['price-area']} data-verbolia-id='price-area'>
                {/** RS Pro Alternatives */}
                {props.data.alternativesRSPro.isVisible && props.data.alternativesRSPro.url && (
                  <Link
                    className={styles['rs-pro-alt-container']}
                    to={props.data.alternativesRSPro.url}
                    onClick={() => rsDeliversClickTracker('RS_ProBanner_PDP')}>
                    <div className={styles['rs-pro-msg']}>
                      <img src={rspro} alt='rs-pro-logo' />
                      <span className={styles['rs-pro-label']}>{getLabel(props.data.labels, 'alternatives_available_from')}</span>
                    </div>
                    <div className={styles.chev}>
                      <Icon name='chev-right' />
                    </div>
                  </Link>
                )}

                {/** Expand search buttons mobile */}
                {isMobileDevice() && expandSearchBtns}

                {state.priceArea && (
                  <div className={styles['price-wrapper']}>
                    <PriceArea
                      labels={props.data.labels}
                      allowAddToPartsList={props.data.allowAddToPartsList}
                      titleArea={state.titleArea}
                      mediaArea={props.data.mediaArea}
                      priceArea={state.priceArea}
                      ppkArea={props.data.ppkArea}
                      calibrationArea={state.calibrationArea}
                      onPackagingChange={updatePackaging}
                      onCalibrationChange={updateCalibration}
                      stockArea={state.stockArea}
                      punchoutInfoArea={props.data.punchoutInfoArea}
                      stickersArea={props.data.stickersArea}
                      selectedPriceBreakIndex={state.selectedPriceBreakIndex}
                      energyArea={props.data.energyArea}
                      locale={props.contextInfo.locale}
                      basketService={props.basketService}
                      pricingService={props.pricingService}
                      onQtyChange={onQtyChange}
                      euElrProductInformationSheet={state.euElrProductInformationSheet}
                      productService={props.productService}
                      onUnavailableEnergyProductInfoSheetBtnClick={() => {
                        if (props.data.titleArea?.stockCode) {
                          // RS documents
                          props.requestSubject?.next({
                            type: PDPQueryType.GetDataSheets,
                            stockCode: props.data.titleArea?.stockCode,
                            documentsType: '1',
                          });
                        }
                      }}
                    />
                  </div>
                )}

                {/* Live stock */}
                {state.stockArea?.statusCode === StockStatus.LocallyManaged ? (
                  unavailableStockInfo
                ) : state.stockArea?.isUsingDTP ? (
                  <Card className={`${styles.stock} ${styles['DTP-stock']}`} data-verbolia-id='product-detail-page-dtp-stock'>
                    {dtpStockBadge}
                    {dtpStockInfo}
                    {state.stockArea.includesDate && (
                      <div className={styles['stock-info-disclaimer']}>
                        <Icon name='info-outline' width={24} height={24} className={styles['info-icon']} />
                        <p>{getLabel(props.data.labels, 'stock_refers_to_uk_warehouse')}</p>
                      </div>
                    )}
                  </Card>
                ) : (
                  getStockBlock()
                )}

                {state.priceArea && state.priceArea.priceBreaks.length > 1 && (
                  <PriceBreakTable
                    labels={props.data.labels}
                    prices={getPriceBreakPrices()}
                    packPriceHeader={props.data.priceArea?.packPriceHeader}
                    selectedQty={state.selectedQuantity}
                  />
                )}

                <div
                  className={
                    isBetterWorldProduct || props.data.config.showDesignSparkArea
                      ? styles['desktop-banners-visible']
                      : styles['desktop-banners-not-visible']
                  }
                  data-verbolia-id='product-detail-page-banners-desktop'>
                  {getBetterWorldBanner()}
                  {getDesignSparkBlock()}
                </div>
              </div>
            </div>
          </div>
          {/* Alternative Products */}
          {<div className={styles['alternative-products-gte-desktop']}>{getAlternativeProductBlock()}</div>}
        </div>

        <div id='product-info-not-desktop' className={`inner-page-wrapper ${styles['product-info']}`}>
          {productCards}
          <div className={styles['right-col']}></div>
        </div>

        <RecentlyViewedComponent
          page='PDP'
          extraData={props.data.titleArea?.stockCode}
          className={`inner-page-wrapper ${styles['recently-viewed']}`}
        />
      </div>

      {state.isSimilarProductsModalOpen && (
        <Modal
          closeCallback={() => {
            dispatch({ type: PDPActionType.ToggleSimilarProductsModaOpen, isOpen: false });
          }}
          headerElt={<span>{getLabel(props.data.labels, 'search_for_similar_products')}</span>}
          headerClassName={styles['similar-products-modal-header']}
          isSidebar={true}
          showSidebarHeaderBottomBorder={false}
          isClosable={true}
          isWhiteBackgroundColor={true}
          className={styles['similar-products-modal']}>
          <div className={styles['similar-products-modal-content']}>
            <p>{getLabel(props.data.labels, 'select_attributes_find_matching_products')}</p>
            <Checkbox
              name='similar-products-checkbox'
              inline={false}
              defaultSelection={state.similarProductsSelectedAttributes}
              spacing={0}
              className={styles['similar-products-modal-checkbox']}
              selectCallback={(keys) => dispatch({ type: PDPActionType.SelectSimilarProductAttribute, keys: keys })}
              list={[
                {
                  key: 'select_all',
                  elt: (
                    <div>
                      <span>{getLabel(props.data.labels, 'select_all')}</span>
                    </div>
                  ),
                },
                ...props.data.attributesArea.attributes
                  // return only attributes with populated nameIds and valueIds
                  .filter((a) => a.nameId && a.nameId.length > 0 && a.valueId && a.valueId.length > 0)
                  .map((attribute) => {
                    return {
                      key: attribute.name,
                      elt: (
                        <div className={styles['similar-products-attribute-container']}>
                          <span>{attribute.name}</span>
                          <span>{attribute.value}</span>
                        </div>
                      ),
                    };
                  }),
              ]}
            />
            <div className={styles['similar-products-modal-content-btn']}>
              <Button
                buttonType='primary'
                text={getLabel(props.data.labels, 'search_for_similar_products_cta').replace(
                  '[total]',
                  state.totalFilteredProducts?.toString() ?? ''
                )}
                disabled={state.similarProductsSelectedAttributes.length === 0}
                size='full-width'
                onClick={() => {
                  if (state.filteredFacets) {
                    rsDeliversClickTracker('Search_Similar_PDP');
                    props.plpService.setUrlHash(
                      history,
                      location,
                      undefined,
                      undefined,
                      undefined,
                      { facets: state.filteredFacets },
                      undefined,
                      props.data.breadcrumb.links[props.data.breadcrumb.links.length - 1].url
                    );
                  }
                }}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );

  function reducer(state: State, action: Action): State {
    switch (action.type) {
      case PDPActionType.ToggleProductDetails: {
        return {
          ...state,
          isProductDetailsOpen: action.isOpen,
        };
      }
      case PDPActionType.UpdateSelectedQty: {
        let selectedPriceBreakIndex = state.priceArea?.priceBreaks.findIndex(
          (p) => action.quantity && action.quantity >= p.minimum && action.quantity <= p.maximum
        );

        if (!(action.quantity && state.priceArea?.priceBreaks?.length) || selectedPriceBreakIndex === -1 /** if not valid index found */) {
          selectedPriceBreakIndex = undefined;
        }

        return {
          ...state,
          selectedQuantity: action.quantity,
          selectedPriceBreakIndex: selectedPriceBreakIndex,
        };
      }
      case PDPActionType.UpdateStockData: {
        return {
          ...state,
          stockArea: action.stockArea,
        };
      }
      case PDPActionType.ToggleSimilarProductsModaOpen: {
        return {
          ...state,
          isSimilarProductsModalOpen: action.isOpen,
          similarProductsSelectedAttributes: [],
        };
      }
      case PDPActionType.SelectSimilarProductAttribute: {
        return {
          ...state,
          similarProductsSelectedAttributes: getSimilarProductsSelectedAttributes(action.keys, state),
        };
      }
      case PDPActionType.SetTotalFilteredProducts: {
        return {
          ...state,
          totalFilteredProducts: action.total,
          filteredFacets: action.filteredFacets,
        };
      }
      case PDPActionType.SetDoProductDetailsNeedTruncation: {
        return {
          ...state,
          doProductDetailsNeedTruncation: action.doProductDetailsNeedTruncation,
        };
      }
      case PDPQueryType.UpdatePPKData: {
        let titleArea = state.titleArea;

        if (titleArea && action.data.titleArea) {
          titleArea = {
            ...titleArea,
            stockCode: action.data.titleArea.stockCode,
            formattedStockCode: action.data.titleArea.formattedStockCode,
          };
        }

        return {
          ...state,
          priceArea: action.data.priceArea,
          stockArea: action.data.stockArea,
          selectedQuantity: action.data.priceArea?.minimumOrderQuantity,
          selectedPriceBreakIndex: undefined,
          titleArea: titleArea,
        };
      }
      case PDPQueryType.UpdateCalibrationData: {
        let titleArea = state.titleArea;

        if (titleArea && action.data.titleArea) {
          titleArea = {
            ...titleArea,
            title: action.data.titleArea.title,
            stockCode: action.data.titleArea.stockCode,
            formattedStockCode: action.data.titleArea.formattedStockCode,
          };
        }

        return {
          ...state,
          priceArea: action.data.priceArea,
          stockArea: action.data.stockArea,
          selectedQuantity: action.data.priceArea?.minimumOrderQuantity,
          selectedPriceBreakIndex: undefined,
          titleArea: titleArea,
        };
      }
      case PDPQueryType.GetDataSheets: {
        let documents = [];
        let euElrProductInformationSheet = state.euElrProductInformationSheet;

        if (action.data.documentsType === '1') {
          const currentDocUrls = state.documentsArea?.documents.map((d) => d.url);

          const euElrProductInformationSheetDoc = action.data.dataSheets.find(
            (d) => d.documentType === DocumentType.ElrProductInformationSheet
          );

          // all documents excluding those already stored in the state
          const remainingNewDocuments = action.data.dataSheets.filter((d) => !currentDocUrls.includes(d.url));

          documents = [...(state.documentsArea?.documents ?? []), ...remainingNewDocuments];
          euElrProductInformationSheet = {
            callNum: (state.euElrProductInformationSheet?.callNum ?? 0) + 1,
            url: euElrProductInformationSheetDoc?.url,
          };
        } else {
          documents = [...(state.documentsArea?.documents ?? []), ...action.data.dataSheets];
        }

        return {
          ...state,
          documentsArea: {
            documents: documents,
          },
          euElrProductInformationSheet: euElrProductInformationSheet,
        };
      }
      case PDPQueryType.GetCadDocuments: {
        return {
          ...state,
          cadDocuments: action.data.cadDocuments,
        };
      }
      case PDPQueryType.GetTracePartsDocumentUrl: {
        window.location.href = action.data.tracePartsDocumentUrl;
        return { ...state };
      }
    }
  }

  function getPriceBreakPrices(): IPDPPriceBreakItem[] {
    let prices: IPDPPriceBreakItem[] | undefined = undefined;

    if (incVat || !props.data.priceArea?.showExVat) {
      prices = state.priceArea?.priceBreaks;
    } else {
      prices = state.priceArea?.priceBreaksExVat;
    }

    if (!prices) prices = [];

    return prices;
  }

  function getStockBlock() {
    if (state.stockArea) {
      return (
        <div className={styles.stock} data-verbolia-id='product-detail-page-stock'>
          <StockStatusCard
            stockStatus={state.stockArea.statusCode}
            title={state.stockArea.title}
            stockMessage={state.stockArea.stockMessage}
            infoMessage={state.stockArea.infoMessage}
            noMobileBorderRadius={true}
          />
        </div>
      );
    }

    return null;
  }

  function getBetterWorldBanner() {
    if (isBetterWorldProduct) {
      return (
        <Card className={styles['better-world-card']}>
          <div className={styles['better-world-card-title']}>
            <Icon name='sustainable' width={36} height={36} />
            <span>{productTypes.find((p) => p.mode === 'SUSTAINABLE')?.title}</span>
          </div>
          <div>{getLabel(props.data.labels, 'better_world_description')}</div>
          <Button
            buttonType='link'
            text={getLabel(props.data.labels, 'better_world_click_here')}
            className={styles['better-world-btn']}
            href={getLabel(props.data.labels, 'better_world_url')}
          />
        </Card>
      );
    }
  }

  //show design spark card for the categories that have 3D Downloads
  function getDesignSparkBlock() {
    if (props.data.config.showDesignSparkArea) {
      return (
        <Card className={styles['design-spark-card']}>
          <div className={styles['design-spark-title-container']}>
            <div ref={designSparkTitleRef} className={styles['design-spark-title-area']}>
              <DesignSparkLogo />
              <p className={styles['title-1']}>{getLabel(props.data.labels, 'design_spark_ideate_create_collaborate')}</p>
              <div className={styles['design-spark-title-sub-area']}>
                <p className={styles['title-2']}>{getLabel(props.data.labels, 'design_spark_join_for_free')}</p>
                <p className={styles['title-3']}>{getLabel(props.data.labels, 'design_spark_no_hidden_fees')}</p>
              </div>
            </div>
            {!isMobileDevice() && (
              <>
                <div className={`${styles['design-spark-image-container']} ${styles['design-spark-image-desktop']}`}>
                  <img src={DesignSparkImage} alt='design-spark' className={styles['design-spark-image']} />
                </div>

                <div className={`${styles['design-spark-image-container']} ${styles['design-spark-image-tablet']}`}>
                  <img src={DesignSparkImageTablet} alt='design-spark' className={styles['design-spark-image']} />
                </div>
              </>
            )}
          </div>
          <div className={styles['design-spark-details']}>
            <ul>
              <li>{getLabel(props.data.labels, 'design_spark_download_and_use')}</li>
              <li>{getLabel(props.data.labels, 'design_spark_view_and_contribute')}</li>
              <li>{getLabel(props.data.labels, 'design_spark_download_schematics')}</li>
            </ul>
          </div>
          <Button
            className={styles['design-spark-button']}
            text={getLabel(props.data.labels, 'click_to_find_out_more')}
            icon='external'
            iconPosition='right'
            isExternalLink={true}
            href={getLabel(props.data.labels, 'design_spark_url')}
            target='_blank'
            rel='noopener noreferrer' //to avoid tabnapping https://www.freecodecamp.org/news/how-to-use-html-to-open-link-in-new-tab/
            onClick={() => rsDeliversClickTracker('DESIGNSPARK_LINK_PDP')}
          />
        </Card>
      );
    }

    return null;
  }

  function getAlternativeProductBlock() {
    return props.data.alternativeProducts.length ? (
      <AlternativeProducts
        labels={props.data?.labels ?? undefined}
        mainProductStockCode={props.data.titleArea?.stockCode ?? ''}
        // Mock product data
        products={props.data.alternativeProducts}
        incVat={incVat}
        productService={props.productService}
        basketService={props.basketService}
        className={styles['alternative-products']}
      />
    ) : (
      <></>
    );
  }

  function getSimilarProductsSelectedAttributes(attributes: (string | number)[], state: State) {
    let attributesCopy = attributes;

    if (attributes.includes('select_all')) {
      if (state.similarProductsSelectedAttributes.includes('select_all')) {
        attributesCopy = attributes.filter((k) => k !== 'select_all');
      } else {
        attributesCopy = ['select_all', ...props.data.attributesArea.attributes.map((a) => a.name)];
      }
    } else if (!attributes.includes('select_all') && state.similarProductsSelectedAttributes?.includes('select_all')) {
      attributesCopy = [];
    }

    return attributesCopy;
  }

  function getMedia(): IPDPMedia {
    if (props.data.mediaArea) {
      if (props.data.mediaArea.showPCBFootprint && props.data.mediaArea.pcbFootprintParameters) {
        // to avoid copying over references, otherwise will keep pushing data to array
        let mediaWPCB = JSON.parse(JSON.stringify(props.data.mediaArea));
        mediaWPCB.media.push(props.data.mediaArea.pcbFootprintParameters);
        return mediaWPCB;
      }
      return props.data.mediaArea;
    }
    return { media: [], showPCBFootprint: false, mediaUrl: '', disclaimerMessage: '', isZoomEnabled: false };
  }

  function updatePackaging(stockCode: string) {
    props.requestSubject?.next({ type: PDPQueryType.UpdatePPKData, stockCode });
  }

  function updateCalibration(stockCode: string) {
    if (stockCode !== state.titleArea?.stockCode) {
      props.requestSubject?.next({ type: PDPQueryType.UpdateCalibrationData, stockCode });
    }
  }

  function onQtyChange(quantity?: number) {
    dispatch({ type: PDPActionType.UpdateSelectedQty, quantity: quantity });
  }

  function getTracePartsDocumentUrl(documentId: number) {
    if (props.data.titleArea?.stockCode)
      props.requestSubject?.next({
        type: PDPQueryType.GetTracePartsDocumentUrl,
        stockCode: props.data.titleArea?.stockCode,
        documentId: documentId,
        tracePartsPartId: props.data.documentsArea.documents.find((d) => d.documentType === DocumentType.TraceParts3DCad)?.tracePartsPartId,
      });
  }

  function rsDeliversClickTracker(info: string) {
    const eventData = {
      __Info: info,
      __Result: props.data.titleArea?.stockCode,
    };

    props.productService.clickEvent(eventData);
  }

  function clickBackToResults() {
    clickEvent({
      __Type: 'buttonTracker',
      __Info: 'BackToResultsPDP',
      __Result: `${props.data.titleArea?.familyId},${props.data.titleArea?.stockCode}`,
    });
  }
};
